/**
 * Unified Editor Page
 *
 * Merges the IDF Code Editor and Visual Object Editor into a single page
 * with three synced views: Code, Card, and Table.
 */

import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useIDFStore } from '@/stores/idfStore';
import { useIDDStore } from '@/stores/iddStore';
import { useVisualObjectEditorStore } from '@/visual-object-editor/visualObjectEditorStore';
import { IDFEditor, EditorProvider, useEditorCommands, useEditorReady } from '@/editor';
import { DropZone } from '@/storage';
import { SanityCheckPanel } from '@/ai';
import { useIsMobile } from '@/hooks';
import { cn } from '@/lib/utils';
import { UnifiedObjectBrowser } from './UnifiedObjectBrowser';
import { ObjectEditorCard, ObjectEditorGrid } from '@/visual-object-editor/ObjectEditorCard';
import { BulkPropertiesPanel } from '@/visual-object-editor/BulkPropertiesPanel';
import { ObjectCreationWizard } from '@/visual-object-editor/ObjectCreationWizard';
import { FileRequiredEmptyState } from './FileRequiredEmptyState';
import { ResizablePanel } from './ResizablePanel';
import type { IDFObject } from '@/parser/types';

/** View mode type */
type EditorViewMode = 'code' | 'cards' | 'table';

/**
 * View mode toggle buttons
 */
function ViewModeToggle({
  mode,
  onChange,
  compact = false,
}: {
  mode: EditorViewMode;
  onChange: (mode: EditorViewMode) => void;
  compact?: boolean;
}) {
  return (
    <div className="flex rounded-lg border border-border bg-muted p-0.5" data-onboarding="view-mode-toggle">
      <button
        onClick={() => onChange('code')}
        className={cn(
          'flex items-center gap-1 rounded-md text-sm font-medium transition-colors',
          compact ? 'px-1.5 py-0.5' : 'px-2 py-1',
          mode === 'code'
            ? 'bg-card text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        )}
        title="Code view"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z" />
        </svg>
        {!compact && <span className="hidden sm:inline">Code</span>}
      </button>
      <button
        onClick={() => onChange('cards')}
        className={cn(
          'flex items-center gap-1 rounded-md text-sm font-medium transition-colors',
          compact ? 'px-1.5 py-0.5' : 'px-2 py-1',
          mode === 'cards'
            ? 'bg-card text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        )}
        title="Card view"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M4 5v14h6V5H4zm8 0v6h8V5h-8zm0 8v6h8v-6h-8z" />
        </svg>
        {!compact && <span className="hidden sm:inline">Cards</span>}
      </button>
      <button
        onClick={() => onChange('table')}
        className={cn(
          'flex items-center gap-1 rounded-md text-sm font-medium transition-colors',
          compact ? 'px-1.5 py-0.5' : 'px-2 py-1',
          mode === 'table'
            ? 'bg-card text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        )}
        title="Table view"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 3h18v18H3V3zm2 2v4h6V5H5zm8 0v4h6V5h-6zm-8 6v4h6v-4H5zm8 0v4h6v-4h-6zm-8 6v4h6v-4H5zm8 0v4h6v-4h-6z" />
        </svg>
        {!compact && <span className="hidden sm:inline">Table</span>}
      </button>
    </div>
  );
}

/**
 * Table view for objects
 */
function ObjectTableView({
  objects,
  selectedIds,
  onSelect,
  onSelectAll,
}: {
  objects: IDFObject[];
  selectedIds: string[];
  onSelect: (id: string, multi: boolean) => void;
  onSelectAll: (ids: string[]) => void;
}) {
  const schema = useIDDStore((state) => state.schema);

  if (!objects || objects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <p className="text-lg font-medium">No objects to display</p>
      </div>
    );
  }

  const allSelected = objects.length > 0 && objects.every((obj) => selectedIds.includes(obj.id));
  const someSelected = objects.some((obj) => selectedIds.includes(obj.id));

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={() => {
                  if (allSelected) {
                    onSelectAll([]);
                  } else {
                    onSelectAll(objects.map((obj) => obj.id));
                  }
                }}
                className="rounded border-border"
                title={allSelected ? 'Deselect all' : 'Select all'}
              />
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Name
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground hidden sm:table-cell">
              Class
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground hidden md:table-cell">
              Group
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground hidden sm:table-cell">
              Fields
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-card">
          {objects.map((obj) => {
            const classInfo = schema.objectTypes.get(obj.className.toLowerCase());
            const isSelected = selectedIds.includes(obj.id);

            return (
              <tr
                key={obj.id}
                onClick={(e) => {
                  // Don't handle if clicking the checkbox directly
                  if ((e.target as HTMLElement).tagName === 'INPUT') return;
                  onSelect(obj.id, e.ctrlKey || e.metaKey);
                }}
                className={cn(
                  'cursor-pointer',
                  isSelected ? 'bg-primary/10' : 'hover:bg-muted'
                )}
              >
                <td className="px-4 py-2">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      onSelect(obj.id, true);
                    }}
                    className="rounded border-border"
                  />
                </td>
                <td className="px-4 py-2 text-sm font-medium text-foreground">
                  {obj.fields[0]?.value || '(unnamed)'}
                </td>
                <td className="px-4 py-2 text-sm text-muted-foreground hidden sm:table-cell">
                  {obj.className}
                </td>
                <td className="px-4 py-2 text-sm text-muted-foreground hidden md:table-cell">
                  {classInfo?.group || 'Unknown'}
                </td>
                <td className="px-4 py-2 text-sm text-muted-foreground hidden sm:table-cell">
                  {obj.fields.length}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Properties panel for selected object
 */
function PropertiesPanel({ objectId }: { objectId: string | null }) {
  const document = useIDFStore((state) => state.document);
  const selectedObject = useMemo(() => {
    if (!objectId || !document) return null;
    return document.objects.find((o) => o.id === objectId);
  }, [objectId, document]);

  if (!selectedObject) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-muted-foreground">
        <svg
          className="h-12 w-12 text-muted-foreground/50"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p className="mt-2 text-sm">Select an object to view details</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      <ObjectEditorCard object={selectedObject} selected />
    </div>
  );
}

/**
 * Mobile drawer component for sidebars
 */
function MobileDrawer({
  isOpen,
  onClose,
  title,
  side = 'left',
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  side?: 'left' | 'right';
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-x-0 top-14 bottom-[60px] z-50 flex">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={cn(
          'relative w-80 max-w-[85vw] bg-card shadow-xl flex flex-col',
          side === 'left' ? 'mr-auto' : 'ml-auto'
        )}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="font-semibold text-foreground">{title}</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Empty state component wrapper
 */
function EmptyState({
  hasDocument,
  hasSchema,
  isLoadingSchema,
  schemaError,
  onRetrySchema,
  onNewObject,
}: {
  hasDocument: boolean;
  hasSchema: boolean;
  isLoadingSchema: boolean;
  schemaError: string | null;
  onRetrySchema: () => void;
  onNewObject: () => void;
}) {
  // Schema loading in progress or not loaded yet
  if (isLoadingSchema || !hasSchema) {
    if (schemaError) {
      return (
        <FileRequiredEmptyState
          reason="schema-error"
          error={schemaError}
          action={{
            label: 'Retry Loading Schema',
            onClick: onRetrySchema,
          }}
        />
      );
    }
    return (
      <FileRequiredEmptyState
        reason="loading-schema"
        isLoading={isLoadingSchema}
      />
    );
  }

  // No document loaded
  if (!hasDocument) {
    return (
      <FileRequiredEmptyState
        reason="no-file"
        title="No Document Loaded"
        message="Open an IDF file to start editing objects"
      />
    );
  }

  // Document loaded but no objects
  return (
    <FileRequiredEmptyState
      reason="no-objects"
      action={{
        label: 'Create New Object',
        onClick: onNewObject,
      }}
    />
  );
}

/**
 * Inner editor content - needs to be within EditorProvider to use hooks
 */
function UnifiedEditorContent() {
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState<EditorViewMode>('code');
  const [showBrowserDrawer, setShowBrowserDrawer] = useState(false);
  const [showPropertiesDrawer, setShowPropertiesDrawer] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(true);

  // IDF Store state
  const document = useIDFStore((state) => state.document);
  const errors = useIDFStore((state) => state.errors);
  const isParsed = useIDFStore((state) => state.isParsed);
  const removeObject = useIDFStore((state) => state.removeObject);
  const duplicateObject = useIDFStore((state) => state.duplicateObject);

  // IDD Store state
  const isSchemaLoaded = useIDDStore((state) => state.isLoaded);
  const schema = useIDDStore((state) => state.schema);
  const isLoading = useIDDStore((state) => state.isLoading);
  const schemaErrors = useIDDStore((state) => state.errors);
  const loadSchemaFromUrl = useIDDStore((state) => state.loadSchemaFromUrl);

  // Visual Object Editor Store state
  const selectedObjectIds = useVisualObjectEditorStore((state) => state.selectedObjectIds);
  const filter = useVisualObjectEditorStore((state) => state.filter);
  const selectObject = useVisualObjectEditorStore((state) => state.selectObject);
  const selectObjects = useVisualObjectEditorStore((state) => state.selectObjects);
  const clearSelection = useVisualObjectEditorStore((state) => state.clearSelection);
  const openWizard = useVisualObjectEditorStore((state) => state.openWizard);
  const getFilteredObjects = useVisualObjectEditorStore((state) => state.getFilteredObjects);

  // Editor commands for code view navigation
  const commands = useEditorCommands();
  const isEditorReady = useEditorReady();

  // Ref to track if we've already initiated schema loading
  const hasInitiatedLoading = useRef(false);

  // Load schema on mount if not already loaded
  useEffect(() => {
    const schemaIsPopulated = schema.objectTypes.size > 0;
    if (!schemaIsPopulated && !isLoading && !hasInitiatedLoading.current) {
      hasInitiatedLoading.current = true;
      loadSchemaFromUrl('/energyplus/Energy+.idd');
    }
  }, [schema.objectTypes.size, isLoading, loadSchemaFromUrl]);

  // Get filtered objects for card/table views
  const filteredObjects = useMemo(() => {
    return getFilteredObjects(document, isSchemaLoaded ? schema : null);
  }, [document, schema, isSchemaLoaded, getFilteredObjects, filter]);

  // Handle object selection - sync with code view
  const handleObjectSelect = useCallback(
    (id: string, multi: boolean) => {
      selectObject(id, multi);

      // If in code view, navigate to the object
      if (viewMode === 'code' && isEditorReady) {
        commands.navigateToObject(id);
      }
    },
    [selectObject, viewMode, isEditorReady, commands]
  );

  // Handle bulk delete
  const handleDeleteSelected = useCallback(() => {
    if (selectedObjectIds.length === 0) return;
    if (
      confirm(
        `Delete ${selectedObjectIds.length} selected object${selectedObjectIds.length !== 1 ? 's' : ''}?`
      )
    ) {
      selectedObjectIds.forEach((id) => removeObject(id));
      clearSelection();
    }
  }, [selectedObjectIds, removeObject, clearSelection]);

  // Handle bulk duplicate
  const handleDuplicateSelected = useCallback(() => {
    selectedObjectIds.forEach((id) => duplicateObject(id));
  }, [selectedObjectIds, duplicateObject]);

  // Handle new object
  const handleNewObject = useCallback(() => {
    openWizard();
  }, [openWizard]);

  // Handle schema retry
  const handleRetrySchema = useCallback(() => {
    hasInitiatedLoading.current = false;
    loadSchemaFromUrl('/energyplus/Energy+.idd');
  }, [loadSchemaFromUrl]);

  // Get schema error message
  const schemaErrorMessage = useMemo(() => {
    if (schemaErrors.length === 0) return null;
    return schemaErrors[0]?.message || 'Unknown error loading schema';
  }, [schemaErrors]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      if (e.key === 'Escape') {
        clearSelection();
      }
      if (e.key === 'Delete' && selectedObjectIds.length > 0 && viewMode !== 'code') {
        handleDeleteSelected();
      }
      // Ctrl+1/2/3 to switch views
      if ((e.ctrlKey || e.metaKey) && e.key === '1') {
        e.preventDefault();
        setViewMode('code');
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '2') {
        e.preventDefault();
        setViewMode('cards');
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '3') {
        e.preventDefault();
        setViewMode('table');
      }
      // Ctrl/Cmd+A to select all (only in card/table view, not code view)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && viewMode !== 'code') {
        e.preventDefault();
        selectObjects(filteredObjects.map((obj) => obj.id));
      }
      // Tab/Shift+Tab to navigate between objects (only in card/table view)
      if (e.key === 'Tab' && viewMode !== 'code' && filteredObjects.length > 0) {
        e.preventDefault();
        const currentIndex = selectedObjectIds.length > 0
          ? filteredObjects.findIndex((obj) => obj.id === selectedObjectIds[0])
          : -1;

        if (e.shiftKey) {
          // Shift+Tab: go to previous object (wrap to end)
          const prevIndex = currentIndex <= 0 ? filteredObjects.length - 1 : currentIndex - 1;
          const prevObject = filteredObjects[prevIndex];
          if (prevObject) selectObject(prevObject.id, false);
        } else {
          // Tab: go to next object (wrap to start)
          const nextIndex = currentIndex >= filteredObjects.length - 1 ? 0 : currentIndex + 1;
          const nextObject = filteredObjects[nextIndex];
          if (nextObject) selectObject(nextObject.id, false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clearSelection, selectedObjectIds, handleDeleteSelected, viewMode, filteredObjects, selectObject, selectObjects]);

  const hasObjects = filteredObjects.length > 0;
  const firstSelectedId: string | null =
    selectedObjectIds.length > 0 ? selectedObjectIds[0] ?? null : null;

  // Determine right panel content based on view mode
  const showAIPanelInView = viewMode === 'code' && showAIPanel;

  return (
    <DropZone className={isMobile ? 'h-full' : 'h-[calc(100dvh-3.5rem)]'}>
      <div className="flex h-full flex-col">
        {/* Toolbar */}
        <div className="flex items-center gap-2 border-b bg-card px-2 md:px-4 py-2">
          {/* Mobile: Browser toggle */}
          {isMobile && (
            <button
              onClick={() => setShowBrowserDrawer(true)}
              className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Open object browser"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 6h16M4 12h16M4 18h7" />
              </svg>
            </button>
          )}

          <div className="flex-1" />

          <ViewModeToggle mode={viewMode} onChange={setViewMode} compact={isMobile ?? false} />

          {/* Selection actions */}
          {selectedObjectIds.length > 0 && viewMode !== 'code' && (
            <div className="flex items-center gap-1 border-l border-border pl-2">
              <span className="text-xs text-muted-foreground">
                {selectedObjectIds.length} selected
              </span>
              <button
                onClick={handleDuplicateSelected}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                title="Duplicate selected"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                </svg>
              </button>
              <button
                onClick={handleDeleteSelected}
                className="rounded p-1 text-destructive hover:bg-destructive/10"
                title="Delete selected"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                </svg>
              </button>
            </div>
          )}

          {/* Status */}
          <div className="hidden sm:flex items-center gap-3 text-sm">
            {isParsed && document && (
              <span className="text-muted-foreground">{document.objects.length} objects</span>
            )}
            {errors.length > 0 && (
              <span className="text-red-500">
                {errors.filter((e) => e.severity === 'error').length} errors
              </span>
            )}
            {errors.filter((e) => e.severity === 'warning').length > 0 && (
              <span className="text-yellow-500">
                {errors.filter((e) => e.severity === 'warning').length} warnings
              </span>
            )}
          </div>

          {/* AI Panel toggle (code view only) */}
          {viewMode === 'code' && !isMobile && (
            <button
              onClick={() => setShowAIPanel(!showAIPanel)}
              className={cn(
                'rounded p-1.5 transition-colors',
                showAIPanel
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
              title={showAIPanel ? 'Hide AI panel' : 'Show AI panel'}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </button>
          )}

          {/* Mobile: Properties toggle */}
          {isMobile && (
            <button
              onClick={() => setShowPropertiesDrawer(true)}
              className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Open properties panel"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3h7a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-7m0-18H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7m0-18v18" />
              </svg>
            </button>
          )}
        </div>

        {/* Main content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left sidebar - Object Browser */}
          {!isMobile && (
            <ResizablePanel
              side="left"
              defaultWidth={256}
              minWidth={180}
              maxWidth={400}
              storageKey="unified-editor-left-panel"
              className="overflow-y-auto"
              resizeAriaLabel="Resize object browser panel"
            >
              <UnifiedObjectBrowser
                viewMode={viewMode}
                onNewObject={handleNewObject}
                onObjectSelect={handleObjectSelect}
              />
            </ResizablePanel>
          )}

          {/* Center - Main Content Area */}
          <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
            {viewMode === 'code' ? (
              <>
                {!isMobile && (
                  <div className="flex items-center justify-between border-b px-4 py-2">
                    <h2 className="text-sm font-semibold">IDF Code Editor</h2>
                    {document?.version && (
                      <span className="text-xs text-muted-foreground">
                        EnergyPlus v{document.version}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex-1">
                  <IDFEditor height="100%" autoParse parseDelay={300} />
                </div>
              </>
            ) : (
              <div className="flex-1 overflow-y-auto bg-muted p-3 md:p-6">
                {isLoading || !isSchemaLoaded || !hasObjects ? (
                  <EmptyState
                    hasDocument={!!document}
                    hasSchema={isSchemaLoaded}
                    isLoadingSchema={isLoading}
                    schemaError={schemaErrorMessage}
                    onRetrySchema={handleRetrySchema}
                    onNewObject={handleNewObject}
                  />
                ) : viewMode === 'cards' ? (
                  <ObjectEditorGrid
                    objects={filteredObjects}
                    selectedIds={selectedObjectIds}
                    onSelect={handleObjectSelect}
                  />
                ) : (
                  <ObjectTableView
                    objects={filteredObjects}
                    selectedIds={selectedObjectIds}
                    onSelect={handleObjectSelect}
                    onSelectAll={selectObjects}
                  />
                )}
              </div>
            )}
          </div>

          {/* Right sidebar - Properties Panel or AI Panel */}
          {!isMobile && (
            <>
              {/* Properties Panel - always show in card/table view, or in code view when object selected */}
              {(viewMode !== 'code' || firstSelectedId) && (
                <ResizablePanel
                  side="right"
                  defaultWidth={320}
                  minWidth={240}
                  maxWidth={500}
                  storageKey="unified-editor-properties-panel"
                  className="flex flex-col min-h-0"
                  resizeAriaLabel="Resize properties panel"
                >
                  <div className="border-b border-border px-4 py-2 flex-shrink-0 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-foreground">
                      {selectedObjectIds.length > 1 ? 'Bulk Edit' : 'Properties'}
                    </h2>
                    {selectedObjectIds.length > 1 && (
                      <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                        {selectedObjectIds.length} selected
                      </span>
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto min-h-0">
                    {selectedObjectIds.length > 1 ? (
                      <BulkPropertiesPanel selectedObjectIds={selectedObjectIds} />
                    ) : (
                      <PropertiesPanel objectId={firstSelectedId} />
                    )}
                  </div>
                </ResizablePanel>
              )}

              {/* AI Panel - code view only */}
              {showAIPanelInView && !firstSelectedId && (
                <ResizablePanel
                  side="right"
                  defaultWidth={320}
                  minWidth={240}
                  maxWidth={500}
                  storageKey="unified-editor-ai-panel"
                  className="overflow-y-auto"
                  hideOnMobile={true}
                  mobileBreakpoint="xl"
                  resizeAriaLabel="Resize AI panel"
                >
                  <SanityCheckPanel />
                </ResizablePanel>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile drawers */}
      {isMobile && (
        <>
          <MobileDrawer
            isOpen={showBrowserDrawer}
            onClose={() => setShowBrowserDrawer(false)}
            title="Object Browser"
            side="left"
          >
            <UnifiedObjectBrowser
              viewMode={viewMode}
              onNewObject={() => {
                handleNewObject();
                setShowBrowserDrawer(false);
              }}
              onObjectSelect={(id, multi) => {
                handleObjectSelect(id, multi);
                setShowBrowserDrawer(false);
              }}
            />
          </MobileDrawer>

          <MobileDrawer
            isOpen={showPropertiesDrawer}
            onClose={() => setShowPropertiesDrawer(false)}
            title={selectedObjectIds.length > 1 ? `Bulk Edit (${selectedObjectIds.length})` : 'Properties'}
            side="right"
          >
            {selectedObjectIds.length > 1 ? (
              <BulkPropertiesPanel selectedObjectIds={selectedObjectIds} />
            ) : (
              <PropertiesPanel objectId={firstSelectedId} />
            )}
          </MobileDrawer>
        </>
      )}

      {/* Object Creation Wizard */}
      <ObjectCreationWizard />
    </DropZone>
  );
}

/**
 * Unified Editor Page Component
 */
export function UnifiedEditorPage() {
  return (
    <EditorProvider>
      <UnifiedEditorContent />
    </EditorProvider>
  );
}
